import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo, useState } from 'react'
import { type CamelCasedProperties } from 'type-fest'
import { useStore } from 'zustand'

import BusyOverlay from 'components/common/BusyOverlay'
import useAuth from 'hooks/use-auth'
import { appConfig } from 'utils/app-config'

export type GremlinClientFlags = CamelCasedProperties<
  typeof DEFAULT_FEATURE_FLAG_VALUES
>
export type GremlinClientFlagsPartial = Partial<GremlinClientFlags>

/**
 * This list will provide sane default values for feature flags in the event that LD has an outage
 * and the LD SDK can't retrieve the feature flags from LD's servers
 */
export const DEFAULT_FEATURE_FLAG_VALUES = {
  'reliability-intelligence-analysis-chat': false,
  // INTENDED FOR LONGER-TERM USE
  'max-node-nesting': 5, // not a boolean value - 5 is the initial default
  // ACTIVELY USED FEATURE FLAGS BELOW - DO NOT REMOVE
  'aws-scenario-fi-integration': false,
  'create-service-from-scenario': true,
  'override-container-selection': false,
  'tpm-service-descriptions': false,
  // NEED MORE INFO ON THESE FLAGS
  'cancel-subscription': false
}

function useUserContext() {
  const { currentTeamId, currentTeam, companyId, session } = useAuth()

  const userContext = useMemo(
    () => ({
      key: session.identifier,
      email: session.identifier,
      custom: {
        companyId: companyId,
        companyName: session.company_name,
        teamName: currentTeam.name,
        teamId: currentTeamId,
        role: currentTeam.role
      }
    }),
    [
      companyId,
      currentTeam.name,
      currentTeam.role,
      currentTeamId,
      session.company_name,
      session.identifier
    ]
  )
  return userContext
}

const LaunchDarklyClientLoader: React.FC = ({ children }) => {
  const ldClient = useLDClient()
  const userContext = useUserContext()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    async function identify(user) {
      try {
        await ldClient?.identify(user)
      } catch (e) {
        // swallow ld error
      }
    }

    if (isReady) {
      identify(userContext)
    }
  }, [
    isReady,
    userContext,
    ldClient,
    userContext.custom.teamId,
    userContext.email
  ])

  useEffect(() => {
    ldClient
      ?.waitUntilReady()
      .then(() => {
        setIsReady(true)
        // Set on window like the default client would so we can use this in other areas
        window.ldClient = ldClient
      })
      .catch(() => {}) // swallow ld error
  }, [ldClient])

  return isReady ? <>{children}</> : <BusyOverlay />
}

export const LaunchDarklyProvider: React.FC<{ isEnabled?: boolean }> = ({
  children,
  isEnabled
}) => {
  const userContext = useUserContext()
  const ldClientId = useStore(
    appConfig,
    (state) => state.LAUNCHDARKLY_CLIENT_SIDE_ID
  )

  if (!isEnabled) {
    return <>{children}</>
  }

  const isRunningInCypress = Boolean(window.Cypress)
  const ldOptions = isRunningInCypress
    ? { streaming: false, sendEvents: false }
    : undefined

  return (
    <LDProvider
      clientSideID={ldClientId}
      context={{ ...userContext, kind: 'user' }}
      deferInitialization={false}
      flags={DEFAULT_FEATURE_FLAG_VALUES}
      options={ldOptions}
    >
      <LaunchDarklyClientLoader>{children}</LaunchDarklyClientLoader>
    </LDProvider>
  )
}
